import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import './style/Login.css';

const Login = () => {
    const navigate = useNavigate();
    
    // --- Page State ---
    const [showLoader, setShowLoader] = useState(true);
    const [websiteData, setWebsiteData] = useState({
        logo: '',
        name: 'CashBolt Media',
        tagline: 'Welcome back, Partner'
    });

    // --- Form State ---
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [loginLoading, setLoginLoading] = useState(false);

    // --- Forgot Password & OTP State ---
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    
    // OTP Specific State
    const [forgotOtpSent, setForgotOtpSent] = useState(false);
    const [forgotOtpValues, setForgotOtpValues] = useState(new Array(6).fill(""));
    const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);

    // --- Captcha State ---
    const [captchaOptions, setCaptchaOptions] = useState([]);
    const [targetCaptcha, setTargetCaptcha] = useState(null);
    const [selectedCaptcha, setSelectedCaptcha] = useState(null);

    // --- Captcha Generator ---
    const generateCaptcha = () => {
        const icons = [
            { id: 1, symbol: '🚀', name: 'Rocket' },
            { id: 2, symbol: '💎', name: 'Diamond' },
            { id: 3, symbol: '⚡', name: 'Bolt' },
            { id: 4, symbol: '🛡️', name: 'Shield' },
            { id: 5, symbol: '🔥', name: 'Fire' },
            { id: 6, symbol: '👑', name: 'Crown' }
        ];
        const shuffled = icons.sort(() => 0.5 - Math.random()).slice(0, 4);
        const target = shuffled[Math.floor(Math.random() * shuffled.length)];
        
        setCaptchaOptions(shuffled);
        setTargetCaptcha(target);
        setSelectedCaptcha(null);
    };

    useEffect(() => {
        generateCaptcha();
        document.title = "Login | CashBolt Media";
    }, []);

    // Handle Login Inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }
        if (!selectedCaptcha || selectedCaptcha.id !== targetCaptcha.id) {
            newErrors.captcha = 'Please select the correct icon';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // --- Submit Login (Standard) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            const form = document.querySelector('.login-card-unique');
            form.classList.add('shake-anim-unique');
            setTimeout(() => form.classList.remove('shake-anim-unique'), 500);
            return;
        }

        setLoginLoading(true);

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'affLogin',
                email: formData.email,
                password: formData.password
            });

            if (response.data.status === true) {
                toast.success(response.data.message || 'Login Successful!');
                // Set Cookie (if standard login also needs it, though usually Verify OTP does this for reset flow)
                document.cookie = `authToken=${response.data.authToken};expires=${new Date(Date.now()+365*24*60*60*1000).toUTCString()};path=/`;

                
                navigate('/');
            } else {
                toast.error(response.data.message || 'Invalid Credentials');
                setErrors(prev => ({ ...prev, form: response.data.message }));
                generateCaptcha();
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Server connection failed.';
            toast.error(msg);
            setErrors(prev => ({ ...prev, form: msg }));
        } finally {
            setLoginLoading(false);
        }
    };

    // --- Forgot Password: Send OTP ---
    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        if (!forgotEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
            toast.warning("Please enter a valid email address");
            return;
        }
        setForgotLoading(true);
        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'sendOtp',
                email: forgotEmail
            });
            if (response.data.status === true) {
                toast.success('OTP sent to your email!');
                setForgotOtpSent(true); // Switch UI to OTP input
            } else {
                toast.error(response.data.message || 'Failed to send OTP');
            }
        } catch (error) {
            toast.error('Network error. Please try again.');
        } finally {
            setForgotLoading(false);
        }
    };

    // --- Forgot Password: OTP Input Logic ---
    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;
        const newOtp = [...forgotOtpValues];
        newOtp[index] = element.value;
        setForgotOtpValues(newOtp);
        if (element.value && element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            if (!forgotOtpValues[index] && e.target.previousSibling) {
                e.target.previousSibling.focus();
            }
        }
    };

    // --- Forgot Password: Verify OTP ---
    const handleOtpVerify = async () => {
        const otpCode = forgotOtpValues.join('');
        if (otpCode.length < 6) {
            toast.warning('Please enter the full 6-digit code');
            return;
        }

        setVerifyOtpLoading(true);
        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'verifyAction',
                email: forgotEmail,
                otp: otpCode
            });

            if (response.data.status === true) {
                toast.success('Login verified!');
                
                // Set Cookie for 1 year (31536000 seconds)
                document.cookie = `authToken=${response.data.authToken};expires=${new Date(Date.now()+365*24*60*60*1000).toUTCString()};path=/`;


                setShowForgotModal(false);
                navigate('/');
            } else {
                toast.error(response.data.message || 'Invalid OTP');
                setForgotOtpValues(new Array(6).fill("")); // Reset inputs
            }
        } catch (error) {
            toast.error('Verification failed. Please try again.');
        } finally {
            setVerifyOtpLoading(false);
        }
    };

    // Reset modal state when closed
    const closeForgotModal = () => {
        setShowForgotModal(false);
        setForgotOtpSent(false);
        setForgotEmail('');
        setForgotOtpValues(new Array(6).fill(""));
    };

    return (
        <div className="login-page-wrapper-unique">
            
            {showLoader && (
                <Loader 
                    onDataFetched={(data) => setWebsiteData(prev => ({...prev, ...data}))} 
                    onComplete={() => setShowLoader(false)} 
                />
            )}

            <div className="bg-shape-unique shape-1-unique"></div>
            <div className="bg-shape-unique shape-2-unique"></div>

            <div className="login-container-unique">
                
                {/* Branding Side */}
                <div className="login-brand-side-unique">
                    <div className="brand-content-unique">
                        {websiteData.logo && (
                            <img src={websiteData.logo} alt="Logo" className="brand-logo-unique" />
                        )}
                        <h1 className="brand-title-unique animated-title-unique">{websiteData.name}</h1>
                        <p className="brand-tagline-unique">{websiteData.tagline}</p>
                    </div>
                </div>

                {/* Form Side */}
                <div className="login-form-side-unique">
                    <div className="login-card-unique">
                        <div className="form-header-unique">
                            <h2>Welcome Back</h2>
                            <p>Enter your credentials to access your dashboard</p>
                        </div>

                        <form onSubmit={handleSubmit} className="login-form-unique">
                            
                            {/* Email */}
                            <div className="form-row-unique">
                                <div className="input-group-unique">
                                    <input 
                                        type="email" name="email" placeholder=" " 
                                        value={formData.email} onChange={handleChange}
                                        className={errors.email ? 'error-border-unique' : ''}
                                    />
                                    <label>Email Address</label>
                                    {errors.email && <span className="error-msg-unique">{errors.email}</span>}
                                </div>
                            </div>

                            {/* Password */}
                            <div className="form-row-unique">
                                <div className="input-group-unique">
                                    <input 
                                        type="password" name="password" placeholder=" " 
                                        value={formData.password} onChange={handleChange}
                                        className={errors.password ? 'error-border-unique' : ''}
                                    />
                                    <label>Password</label>
                                    {errors.password && <span className="error-msg-unique">{errors.password}</span>}
                                </div>
                            </div>

                            <div className="forgot-wrapper-unique">
                                <span 
                                    className="forgot-link-unique"
                                    onClick={() => setShowForgotModal(true)}
                                >
                                    Forgot Password?
                                </span>
                            </div>

                            {/* Captcha */}
                            <div className="captcha-container-unique">
                                <p className="captcha-label-unique">
                                    Security Check: Select the <span>{targetCaptcha?.name}</span>
                                </p>
                                <div className="captcha-grid-unique">
                                    {captchaOptions.map((opt) => (
                                        <div 
                                            key={opt.id}
                                            className={`captcha-item-unique ${selectedCaptcha?.id === opt.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedCaptcha(opt)}
                                        >
                                            {opt.symbol}
                                        </div>
                                    ))}
                                </div>
                                {errors.captcha && <div className="error-msg-unique center-unique">{errors.captcha}</div>}
                            </div>

                            {errors.form && <div className="error-msg-unique center-unique">{errors.form}</div>}

                            <button type="submit" className="submit-btn-unique wave-effect-unique" disabled={loginLoading}>
                                {loginLoading ? <span className="btn-loader-unique"></span> : (
                                    <>
                                        <span>LOGIN</span>
                                        <div className="wave-unique"></div>
                                    </>
                                )}
                            </button>

                            <div className="login-redirect-unique">
                                Don't have an account? <span onClick={() => navigate('/register')}>Register Here</span>
                            </div>

                        </form>
                    </div>
                </div>
            </div>

            {/* Forgot Password / OTP Modal */}
            {showForgotModal && (
                <div className="modal-overlay-unique" onClick={closeForgotModal}>
                    <div className="modal-content-unique" onClick={(e) => e.stopPropagation()}>
                        
                        <div className="modal-header-unique">
                            <h3>{forgotOtpSent ? 'Verify OTP' : 'Reset Password'}</h3>
                            <button className="close-modal-unique" onClick={closeForgotModal}>&times;</button>
                        </div>
                        
                        <div className="modal-body-unique center-unique">
                            
                            {!forgotOtpSent ? (
                                // --- STEP 1: Enter Email ---
                                <>
                                    <p style={{textAlign:'left'}}>Enter your registered email to receive an OTP.</p>
                                    <div className="input-group-unique" style={{marginBottom:'1rem'}}>
                                        <input 
                                            type="email" 
                                            placeholder=" " 
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                        />
                                        <label>Your Email</label>
                                    </div>
                                    <button 
                                        className="modal-submit-unique" 
                                        onClick={handleForgotSubmit}
                                        disabled={forgotLoading}
                                    >
                                        {forgotLoading ? 'SENDING...' : 'SEND OTP'}
                                    </button>
                                </>
                            ) : (
                                // --- STEP 2: Enter OTP ---
                                <>
                                    <p style={{marginBottom:'1.5rem'}}>
                                        Enter the 6-digit code sent to <br/>
                                        <span style={{color: '#fff', fontWeight:'bold'}}>{forgotEmail}</span>
                                    <br/> Check Spam Folder 
                                    </p>
                                    
                                    <div className="otp-input-container">
                                        {forgotOtpValues.map((data, index) => (
                                            <input
                                                key={index}
                                                className="otp-box"
                                                type="text"
                                                name="otp"
                                                maxLength="1"
                                                value={data}
                                                onChange={(e) => handleOtpChange(e.target, index)}
                                                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                                onFocus={(e) => e.target.select()}
                                            />
                                        ))}
                                    </div>

                                    <button 
                                        className="modal-submit-unique" 
                                        onClick={handleOtpVerify}
                                        disabled={verifyOtpLoading}
                                        style={{marginTop: '2rem'}}
                                    >
                                        {verifyOtpLoading ? 'VERIFYING...' : 'VERIFY & LOGIN'}
                                    </button>
                                </>
                            )}

                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Login;